/**
 * Theme preference, held in `localStorage` and mirrored onto `<html data-theme>`.
 *
 * The initial application happens inline in index.html before first paint; this module
 * owns everything after that. Device-local on purpose: public viewers have no account,
 * so a database copy would contradict itself the first time someone switched phones.
 */

import { useCallback, useEffect, useState } from "react";

export type ThemePreference = "system" | "light" | "dark";

const STORAGE_KEY = "aimly.theme";
const CYCLE: readonly ThemePreference[] = ["system", "light", "dark"];

const darkScheme = window.matchMedia("(prefers-color-scheme: dark)");

function isPreference(value: string | null): value is ThemePreference {
  return value === "system" || value === "light" || value === "dark";
}

export function readPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isPreference(stored) ? stored : "system";
  } catch {
    // Storage blocked (private mode): follow the device for this session only.
    return "system";
  }
}

export function writePreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Nothing to do: the theme still applies for the current page.
  }
}

export function isDark(preference: ThemePreference): boolean {
  return preference === "dark" || (preference === "system" && darkScheme.matches);
}

export function applyPreference(preference: ThemePreference): void {
  const root = document.documentElement;
  root.dataset.theme = isDark(preference) ? "dark" : "light";
  root.dataset.themePreference = preference;
}

export function nextPreference(preference: ThemePreference): ThemePreference {
  return CYCLE[(CYCLE.indexOf(preference) + 1) % CYCLE.length];
}

/**
 * While the preference is "system", an OS-level switch is followed live. Registering the
 * listener unconditionally would fight an explicit light/dark choice, so it is attached
 * only for the duration the preference is "system".
 */
export function followSystemTheme(onChange: () => void): () => void {
  const listener = () => onChange();
  darkScheme.addEventListener("change", listener);
  return () => darkScheme.removeEventListener("change", listener);
}

/**
 * React half of the preference: applies on change, follows the OS while the preference is
 * "system", and hands back a cycle function so the toggle is one line wherever it sits.
 */
export function useThemePreference(): [ThemePreference, () => void] {
  const [preference, setPreference] = useState<ThemePreference>(readPreference);

  useEffect(() => {
    applyPreference(preference);
  }, [preference]);

  // Only while the device decides: an explicit light/dark choice must survive an OS switch.
  useEffect(() => {
    if (preference !== "system") return;
    return followSystemTheme(() => applyPreference("system"));
  }, [preference]);

  const cycle = useCallback(() => {
    setPreference((current) => {
      const next = nextPreference(current);
      writePreference(next);
      return next;
    });
  }, []);

  return [preference, cycle];
}
